import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { endpoints } from '../api/client.js';
import { useResource } from '../hooks/useResource.js';
import PageHeader from '../components/PageHeader.jsx';
import { Panel } from '../components/Panel.jsx';
import { Resource } from '../components/StateViews.jsx';
import { Tag, TlsBadge, UnitPill } from '../components/StatusPill.jsx';
import { unitState } from '../lib/status.js';
import PinButton from '../components/PinButton.jsx';
import { usePins } from '../pins/PinsContext.jsx';
import { allDomains, domainName, preferredSlot } from '../lib/slots.js';

export default function Sites() {
  const pins = usePins();
  const load = useCallback((signal) => endpoints.sites(signal), []);
  const state = useResource(load, []);
  const [query, setQuery] = useState('');

  const sites = state.data;
  const filtered = useMemo(() => {
    if (!Array.isArray(sites)) return [];
    const needle = query.trim().toLowerCase();
    const matches = !needle ? sites : sites.filter((site) =>
      [
        site.name, site.tenant, site.kind,
        site.slots?.preview?.unit, site.slots?.live?.unit,
        ...allDomains(site).map(domainName),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle)),
    );
    const order = new Map(pins.sites.map((name, index) => [name, index]));
    return [...matches].sort((a, b) => {
      const aOrder = order.has(a.name) ? order.get(a.name) : Number.MAX_SAFE_INTEGER;
      const bOrder = order.has(b.name) ? order.get(b.name) : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }, [pins.sites, sites, query]);

  const summary = useMemo(() => {
    if (!Array.isArray(sites)) return null;
    const counts = sites.reduce((acc, site) => {
      const key = unitState(preferredSlot(site).unit).label;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .map(([label, count]) => `${count} ${label.toLowerCase()}`)
      .join(' · ');
  }, [sites]);

  return (
    <div className="page">
      <PageHeader
        eyebrow="Registry"
        title="Sites"
        lede="Every site in the registry, with preview and live slot state."
        meta={summary ? <p className="refresh-meta">{summary}</p> : null}
        actions={
          <button type="button" className="btn" onClick={state.refresh}>
            Refresh
          </button>
        }
      />

      <Resource
        state={state}
        loadingLabel="Loading the site registry…"
        loadingRows={5}
        errorTitle="Could not load sites"
        isEmpty={(data) => !Array.isArray(data) || data.length === 0}
        emptyTitle="No sites registered"
        emptyBody="The registry is empty. Sites are created on the host with liftedctl site create."
      >
        {() => (
          <Panel
            title="All sites"
            subtitle={`${filtered.length} of ${sites.length} shown, ${pins.sites.length} of ${pins.limit} pinned`}
            actions={
              <label className="search" htmlFor="site-search">
                <span className="visually-hidden">Filter sites</span>
                <input
                  id="site-search"
                  className="search__input"
                  type="search"
                  placeholder="Filter by name, domain, kind…"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
            }
          >
            {filtered.length === 0 ? (
              <p className="state__body">
                No site matches “{query}”.{' '}
                <button type="button" className="linkbtn" onClick={() => setQuery('')}>
                  Clear the filter
                </button>
                .
              </p>
            ) : (
              <SitesTable sites={filtered} />
            )}
          </Panel>
        )}
      </Resource>
    </div>
  );
}

function SitesTable({ sites }) {
  const navigate = useNavigate();

  return (
    <div className="tablewrap">
      <table className="table table--rows">
        <caption className="visually-hidden">
          Registered sites. Select a site name to open its detail page.
        </caption>
        <thead>
          <tr>
            <th scope="col" className="table__pin"><span className="visually-hidden">Pinned</span></th>
            <th scope="col">Site</th>
            <th scope="col">Kind</th>
            <th scope="col">Preview</th>
            <th scope="col">Live</th>
            <th scope="col">Domains</th>
            <th scope="col">TLS</th>
          </tr>
        </thead>
        <tbody>
          {sites.map((site) => {
            const domains = allDomains(site);
            const secured = domains.filter((entry) => entry.tls).length;
            const allSecured = domains.length > 0 && secured === domains.length;

            return (
              <tr
                key={site.name}
                className="row-link"
                onClick={(event) => {
                  if (event.target.closest('a,button,input')) return;
                  navigate(`/sites/${encodeURIComponent(site.name)}`);
                }}
              >
                <td className="table__pin"><PinButton siteName={site.name} /></td>
                <th scope="row">
                  <Link className="table__link mono" to={`/sites/${encodeURIComponent(site.name)}`}>
                    {site.name}
                  </Link>
                  {site.tenant ? <span className="table__sub">{site.tenant}</span> : null}
                </th>
                <td>
                  <Tag>{site.kind || 'unknown'}</Tag>
                </td>
                {['preview', 'live'].map((slotName) => {
                  const slot = site.slots?.[slotName] || {};
                  return (
                    <td key={slotName}>
                      <UnitPill unit={slot.unit} />
                      <span className="table__sub mono">{slot.version || 'nothing deployed'} · :{slot.port ?? '-'}</span>
                    </td>
                  );
                })}
                <td>
                  {domains.length === 0 ? (
                    <span className="is-muted">no domains</span>
                  ) : (
                    <ul className="domainlist">
                      {domains.map((entry) => (
                        <li key={domainName(entry)} className="mono">
                          {domainName(entry)}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td>
                  {domains.length === 0 ? (
                    <span className="is-muted">-</span>
                  ) : (
                    <TlsBadge
                      secured={allSecured}
                      label={allSecured ? `TLS ${secured}/${domains.length}` : `${secured}/${domains.length}`}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
